import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Trash2, ShieldAlert, AlertOctagon } from "lucide-react";

const CATEGORIES = ["auth", "user", "location", "timeclock", "schedule", "absence", "handover", "announcement", "task", "qm", "chat", "delivery", "recipe", "audit", "branding"];

export default function AuditPage() {
  const [items, setItems] = useState([]);
  const [users, setUsers] = useState([]);
  const [filter, setFilter] = useState({ user_id: "", category: "", from_date: "", to_date: "" });
  const [dangerDialog, setDangerDialog] = useState(null);
  const [password, setPassword] = useState("");
  const [olderDays, setOlderDays] = useState(90);

  const load = async () => {
    const q = new URLSearchParams();
    if (filter.user_id) q.set("user_id", filter.user_id);
    if (filter.category) q.set("category", filter.category);
    if (filter.from_date) q.set("from_date", filter.from_date);
    if (filter.to_date) q.set("to_date", filter.to_date);
    const [r, u] = await Promise.all([api.get(`/audit?${q}`), api.get("/users")]);
    setItems(r.data);
    setUsers(u.data);
  };
  useEffect(() => { load(); }, [filter]);

  const del = async (id) => { if (!window.confirm("Diesen Log löschen?")) return; await api.delete(`/audit/${id}`); await load(); };
  const delOlder = async () => { try { const r = await api.post("/audit/delete-older", { older_than_days: olderDays }); toast.success(`${r.data.deleted} Logs gelöscht`); setDangerDialog(null); await load(); } catch (_e) { toast.error("Fehler"); } };
  const delAll = async () => { try { const r = await api.post("/audit/delete-all", { password }); toast.success(`${r.data.deleted} Logs gelöscht`); setDangerDialog(null); setPassword(""); await load(); } catch (err) { toast.error(err.response?.data?.detail || "Fehler"); } };

  return (
    <div className="space-y-6" data-testid="audit-root">
      <div>
        <div className="overline">Verwaltung</div>
        <h2 className="font-heading text-2xl sm:text-3xl font-bold">Aktivitäts-Logs</h2>
        <p className="text-sm text-slate-500 mt-1">Vollständige Rückverfolgbarkeit aller Systemaktivitäten.</p>
      </div>

      <Card className="border-border/60">
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
            <div>
              <Label className="text-xs">Mitarbeiter</Label>
              <Select value={filter.user_id || "all"} onValueChange={(v) => setFilter({ ...filter, user_id: v === "all" ? "" : v })}>
                <SelectTrigger data-testid="audit-filter-user"><SelectValue placeholder="Alle" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle</SelectItem>
                  {users.map(u => <SelectItem key={u.id} value={u.id}>{u.first_name} {u.last_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Kategorie</Label>
              <Select value={filter.category || "all"} onValueChange={(v) => setFilter({ ...filter, category: v === "all" ? "" : v })}>
                <SelectTrigger data-testid="audit-filter-cat"><SelectValue placeholder="Alle" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle</SelectItem>
                  {CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs">Von</Label><Input type="date" value={filter.from_date} onChange={(e) => setFilter({ ...filter, from_date: e.target.value })} /></div>
            <div><Label className="text-xs">Bis</Label><Input type="date" value={filter.to_date} onChange={(e) => setFilter({ ...filter, to_date: e.target.value })} /></div>
            <div className="flex gap-2 items-end">
              <Button data-testid="audit-danger-older" variant="outline" className="flex-1" onClick={() => setDangerDialog("older")}><ShieldAlert className="w-4 h-4 mr-2" /> Alte löschen</Button>
              <Button data-testid="audit-danger-all" variant="destructive" onClick={() => setDangerDialog("all")}><AlertOctagon className="w-4 h-4" /></Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="border-border/60">
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Zeit</TableHead>
                <TableHead>Benutzer</TableHead>
                <TableHead>IP</TableHead>
                <TableHead>Kategorie</TableHead>
                <TableHead>Aktion</TableHead>
                <TableHead>Beschreibung</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((l) => (
                <TableRow key={l.id} data-testid={`audit-row-${l.id}`}>
                  <TableCell className="text-xs whitespace-nowrap">{new Date(l.created_at).toLocaleString("de-DE")}</TableCell>
                  <TableCell className="font-medium">{l.username || "—"}</TableCell>
                  <TableCell className="text-xs text-slate-500 font-mono">{l.ip || "—"}</TableCell>
                  <TableCell><Badge variant="outline">{l.category}</Badge></TableCell>
                  <TableCell><Badge variant="secondary">{l.action}</Badge></TableCell>
                  <TableCell className="text-sm">{l.description}</TableCell>
                  <TableCell><Button size="icon" variant="ghost" onClick={() => del(l.id)}><Trash2 className="w-4 h-4 text-rose-600" /></Button></TableCell>
                </TableRow>
              ))}
              {items.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-slate-500 py-8">Keine Logs</TableCell></TableRow>}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={dangerDialog === "older"} onOpenChange={() => setDangerDialog(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Alte Logs löschen</DialogTitle></DialogHeader>
          <div>
            <Label>Logs älter als (Tage)</Label>
            <Input type="number" value={olderDays} onChange={(e) => setOlderDays(parseInt(e.target.value || 90))} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDangerDialog(null)}>Abbrechen</Button>
            <Button variant="destructive" data-testid="audit-older-confirm" onClick={delOlder}>Löschen</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={dangerDialog === "all"} onOpenChange={() => setDangerDialog(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle className="text-rose-600">⚠ Alle Logs unwiderruflich löschen</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">Bestätige mit deinem Super-Admin-Passwort:</p>
          <Input data-testid="audit-all-password" type="password" placeholder="Passwort" value={password} onChange={(e) => setPassword(e.target.value)} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setDangerDialog(null)}>Abbrechen</Button>
            <Button variant="destructive" data-testid="audit-all-confirm" onClick={delAll}>Alle löschen</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
